import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import PhoneIcon from '@mui/icons-material/Phone';
import Chip from '@mui/material/Chip';
import { green, grey, red } from '@mui/material/colors';
import Typography from '@mui/material/Typography';
import Box from '@mui/system/Box';
import React, { ChangeEvent, MouseEvent, useState } from 'react';
import { generatePath } from 'react-router';

import OberonCard from '~components/OberonCard';
import OberonSwitch from '~components/OberonSwitch';
import { useAuth } from '~providers/AuthProvider';
import { AccessScope } from '~providers/AuthProvider/domain';
import Routes from '~providers/RouteProvider/Routes';

enum ColorType {
  Active = 'active',
  Inactive = 'inactive',
  InactiveWarning = 'inactive-warning',
}

interface Status {
  borderColor: string;
  statusColor: string;
  defaultListColor: string;
}

interface LeadListCardProps {
  campaignId: number;
  listId: number;
  name: string;
  isActive: boolean;
  isDefaultList: boolean;
  priority: number;
  totalLeads: number;
  numCompleted: number;
  onToggleList: (checked: boolean) => void;
}

const colorData: { [key in ColorType]: Status } = {
  [ColorType.Active]: {
    borderColor: green[600],
    statusColor: green[600],
    defaultListColor: grey[600],
  },
  [ColorType.Inactive]: {
    borderColor: grey[600],
    statusColor: grey[600],
    defaultListColor: grey[600],
  },
  [ColorType.InactiveWarning]: {
    borderColor: grey[600],
    statusColor: grey[600],
    defaultListColor: red[600],
  },
};

const LeadListCard = ({
  ref,
  campaignId,
  listId,
  name,
  isActive,
  isDefaultList,
  priority,
  totalLeads,
  numCompleted,
  onToggleList,
}: LeadListCardProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { hasScope } = useAuth();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const statusName = isActive ? 'Active' : 'Inactive';
  const isDefaultListAndDisabled = isDefaultList === true && isActive === false;
  let colors;

  if (isDefaultListAndDisabled) {
    colors = colorData[ColorType.InactiveWarning];
  } else if (isActive === true) {
    colors = colorData[ColorType.Active];
  } else {
    colors = colorData[ColorType.Inactive];
  }

  // Prevent Link navigation on selecting the switch toggle
  const stopPropagation = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleToggleChange = async (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsUpdating(true);
    await onToggleList(checked);
    setIsUpdating(false);
  };

  return (
    <OberonCard
      ref={ref}
      to={generatePath(Routes.viewCampaignLeadList.path, {
        campaignId: campaignId.toString(),
        listId: listId.toString(),
      })}
      titleFontWeight={400}
      title={name}
      subHeader={
        <>
          <Chip
            sx={{
              marginTop: 2,
              textTransform: 'uppercase',
              fontSize: 10,
              borderRadius: 1,
              height: 20,
              lineHeight: '21px',
              color: '#ffffff',
              fontWeight: 700,
              backgroundColor: colors.statusColor,
            }}
            label={statusName}
          />

          {isDefaultList && (
            <Chip
              sx={{
                marginTop: 2,
                marginLeft: 1,
                textTransform: 'uppercase',
                fontSize: 10,
                borderRadius: 1,
                height: 20,
                lineHeight: '21px',
                color: '#ffffff',
                fontWeight: 700,
                backgroundColor: colors.defaultListColor,
              }}
              label='Default List'
            />
          )}

          {isDefaultListAndDisabled && (
            <Typography
              variant='caption'
              color='textSecondary'
              sx={{
                display: 'block',
                marginTop: 1,
              }}>
              * This list should be enabled as it is the default list for the campaign.
            </Typography>
          )}
        </>
      }
      action={
        hasScope(AccessScope.CanUpdateLeadsListGeneralSettings) && (
          <OberonSwitch
            checked={isActive}
            disabled={isUpdating}
            onClick={stopPropagation}
            onChange={handleToggleChange}
            name={name}
          />
        )
      }
      footerBorderColor={colors.borderColor}
      footer={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant='caption'
            color='textSecondary'
            sx={{
              marginRight: 1,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}>
            <DonutLargeIcon sx={{ marginRight: 0.5 }} fontSize='small' />
            {`${totalLeads > 0 ? ((numCompleted / totalLeads) * 100).toFixed(2) : 0}%`}
          </Typography>

          <Typography
            variant='caption'
            color='textSecondary'
            sx={{
              marginLeft: 1,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}>
            <PhoneIcon sx={{ marginRight: 0.5 }} fontSize='small' />
            {`${numCompleted.toLocaleString()} / ${totalLeads.toLocaleString()}`}
          </Typography>
        </Box>
      }
    />
  );
};

export default LeadListCard;
